<script lang="ts">
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import {
    Send,
    CheckCheck,
    Reply,
    RotateCcw,
    Link2,
    Download,
    Eye,
    Copy,
    Layers,
    History,
    Sheet,
    TriangleAlert,
    Trash2
  } from 'lucide-svelte';
  import { quotationsApi } from '$lib/api/quotations';
  import { session } from '$lib/state/session.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import { getErrorMessage } from '$lib/api/client';
  import { route } from '$lib/state/route.svelte';
  import { formatMoney, formatPercent, bpsToInputValue, parsePercentToBps, clamp } from '$lib/utils/money';
  import * as lifecycle from '$lib/utils/lifecycle';
  import Modal from '$lib/components/ui/Modal.svelte';
  import ShareLinkModal from './ShareLinkModal.svelte';
  import VersionHistoryModal from './VersionHistoryModal.svelte';
  import { BILLING_ENTITIES, BILLING_ENTITY_LABEL, type BillingEntity, type QuotationDetail } from '$lib/types';

  const { quote }: { quote: QuotationDetail } = $props();

  let showShareModal = $state(false);
  let showVersionsModal = $state(false);
  let downloading = $state(false);
  let previewing = $state(false);
  let exportingXlsx = $state(false);
  let exportingBOQ = $state(false);
  let showDiscount = $state(false);

  async function downloadPdf() {
    downloading = true;
    try {
      await quotationsApi.downloadPdf(quote.id, quote.formatted_number, showDiscount);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      downloading = false;
    }
  }

  async function downloadXlsx() {
    exportingXlsx = true;
    try {
      await quotationsApi.downloadXlsx(quote.id, quote.formatted_number);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      exportingXlsx = false;
    }
  }

  async function downloadBOQ() {
    exportingBOQ = true;
    try {
      await quotationsApi.downloadBOQXlsx(quote.id, quote.formatted_number);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      exportingBOQ = false;
    }
  }

  async function previewPdf() {
    previewing = true;
    try {
      await quotationsApi.previewPdf(quote.id, showDiscount);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      previewing = false;
    }
  }

  const queryClient = useQueryClient();
  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['quotation', quote.id] });
    queryClient.invalidateQueries({ queryKey: ['pipeline-quotations'] });
  }

  async function invalidateAfterDelete() {
    queryClient.removeQueries({ queryKey: ['quotation', quote.id] });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['pipeline-quotations'] }),
      queryClient.invalidateQueries({ queryKey: ['approved-quotations'] }),
      queryClient.invalidateQueries({ queryKey: ['approved-quotations-count'] }),
      queryClient.invalidateQueries({ queryKey: ['archived-quotations'] })
    ]);
  }

  const userID = $derived(session.me?.user.id);
  const isAdmin = $derived(session.isAdmin);
  const isOwner = $derived(quote.created_by_id === userID);

  const canSubmit = $derived(lifecycle.canSubmit(quote.status) && (isAdmin || isOwner));
  const canPrice = $derived(isAdmin && !quote.designer_priced && lifecycle.canPrice(quote.status));
  const canApprove = $derived(lifecycle.canApprove(quote, userID, isAdmin));
  const canReturn = $derived(isAdmin && lifecycle.canReturnForPricing(quote.status));
  const canRevise = $derived(lifecycle.canRevise(quote, userID, isAdmin));
  // PDF download: any viewer who can already open this quote (page-level
  // access already gates that) may download it once it's approved — matches
  // the API's own gate (requireQuotationAccess, no extra status check on the
  // /pdf route itself). Share-link management is narrower: only whoever can
  // manage the quote (admin, or the self-serve owning designer) may
  // generate/revoke it — mirrors lifecycle.ManageableBy on the Go side.
  const isApproved = $derived(quote.status === 'approved');
  const canManageShare = $derived(isApproved && lifecycle.manageableBy(quote, userID, isAdmin));
  // Duplicate: same manage-authority gate as the API (lifecycle.ManageableBy
  // — admin, or the owning designer on a self-serve quote), not tied to
  // approval status the way share management is — matches Rails'
  // require_manage_authority! on the duplicate action.
  const canDuplicate = $derived(lifecycle.manageableBy(quote, userID, isAdmin));
  // A new lot remains tied to the same project but is a separate fresh draft,
  // not a revision. Its source may be any status; only its owner or an admin
  // can create it.
  const canCreateNewLot = $derived(isAdmin || isOwner);
  // Permanent deletion follows the API gate: admins may remove any quote;
  // its owner may remove only a still-editable draft/revised quote.
  const canDelete = $derived(isAdmin || (isOwner && lifecycle.canEdit(quote.status)));
  // Both XLSX exports use the same manage-authority API gate
  // (lifecycle.ManageableBy), with no approval-status requirement. The
  // verification sheet is internal and commercial; Export BOQ is the separate
  // production sheet with quantities only.
  const canExportXlsx = $derived(lifecycle.manageableBy(quote, userID, isAdmin));
  // Admin sets the MD pricing-chain percentages (install/commissioning/
  // special discount/tax) — see updateQuotation's admin-only gate in
  // internal/api/handlers/quotations.go. Locked once approved (use Revise
  // first to reopen for edits).
  const chainEditable = $derived(isAdmin && quote.status !== 'approved');

  function runAction(mutFn: () => Promise<unknown>, successMsg: string) {
    mutFn()
      .then(() => {
        toast.success(successMsg);
        invalidate();
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  }

  const submitMut = createMutation(() => ({ mutationFn: () => quotationsApi.submit(quote.id) }));
  const priceMut = createMutation(() => ({ mutationFn: () => quotationsApi.price(quote.id) }));
  const approveMut = createMutation(() => ({ mutationFn: () => quotationsApi.approve(quote.id) }));
  const returnMut = createMutation(() => ({ mutationFn: () => quotationsApi.returnForPricing(quote.id) }));
  const reviseMut = createMutation(() => ({ mutationFn: () => quotationsApi.revise(quote.id) }));

  const duplicateMut = createMutation(() => ({
    mutationFn: () => quotationsApi.duplicate(quote.id),
    onSuccess: (dup) => {
      toast.success('Quotation duplicated. You are now editing the new copy.');
      queryClient.invalidateQueries({ queryKey: ['pipeline-quotations'] });
      route.push({ tab: 'quotations', id: dup.id });
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));
  const newLotMut = createMutation(() => ({
    mutationFn: () => quotationsApi.newLot(quote.id),
    onSuccess: (lot) => {
      toast.success('New lot created for this project. You are now editing it.');
      queryClient.invalidateQueries({ queryKey: ['pipeline-quotations'] });
      route.push({ tab: 'quotations', id: lot.id });
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));
  const deleteMut = createMutation(() => ({
    mutationFn: () => quotationsApi.remove(quote.id),
    onSuccess: async () => {
      toast.success('Quotation permanently deleted.');
      route.replace({ tab: 'pipeline' });
      await invalidateAfterDelete();
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  function deletePermanently() {
    if (window.confirm(`Permanently delete quotation ${quote.formatted_number}? This cannot be undone.`)) {
      deleteMut.mutate();
    }
  }

  const updateChainMut = createMutation(() => ({
    mutationFn: (body: Record<string, unknown>) => quotationsApi.update(quote.id, body),
    onSuccess: invalidate,
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  // Percent chain fields (install/commissioning/special discount/tax) are
  // 0-100%: clamp before saving, and write the clamped value back into the
  // input so it never keeps showing an unsaved/out-of-range value.
  function setChainField(el: HTMLInputElement, field: string, value: string) {
    const bps = clamp(parsePercentToBps(value), 0, 10000);
    el.value = bpsToInputValue(bps);
    updateChainMut.mutate({ [field]: bps });
  }

  // Billing entity (PDF issuer) is admin-only and — per the API's own
  // require_editable_quotation-style bypass — settable even on an approved
  // quote, sent as the ONLY field in the request so the backend recognizes
  // it as a metadata-only change and regenerates the stored approval PDF.
  function setBillingEntity(value: string) {
    updateChainMut.mutate({ billing_entity: value });
  }

  // Partner commission: the share of the giveaway pool paid to whoever
  // introduced the client. NOT a price-chain field — it never changes what the
  // customer pays (see internal/pricing/commission.go). ADMIN-ONLY at the API
  // (updateQuotation's admin block), which is why the input below is too. The
  // 0..100% clamp mirrors the server's.
  //
  // Because admins are exempt from the 40% cap, this panel — not a rejected
  // save — is what has to stop an admin overspending the pool by accident, so
  // the over-cap state is loud rather than a colour change. The figures come
  // from the server (pricing.PartnerCommissionFor via the true list subtotal),
  // never recomputed here.
  const STANDARD_GIVEAWAY_BPS = 4000;
  const giveawayAllowanceBps = $derived(session.me?.user.giveaway_cap_bps ?? STANDARD_GIVEAWAY_BPS);
  // The 40% standard is a reporting threshold, not necessarily this actor's
  // allowance: designated users can be granted a higher cap by an admin.
  const overStandardGiveawayThreshold = $derived(quote.combined_given_bps > STANDARD_GIVEAWAY_BPS);
  const overAllowance = $derived(!isAdmin && quote.vertical !== 'hotel' && quote.combined_given_bps > giveawayAllowanceBps);
  const overBy = $derived(quote.combined_given_bps - STANDARD_GIVEAWAY_BPS);
  // Mirrors lifecycle.ApprovableAtGiveaway: a quote over the pool may only be
  // approved by an admin. Surfaced here so the designer sees WHY before
  // clicking, rather than getting a 403 toast from approveQuotation.
  const approveNeedsAdmin = $derived(overAllowance);

  function setPartnerCommission(el: HTMLInputElement, value: string) {
    const bps = clamp(parsePercentToBps(value), 0, 10000);
    el.value = bpsToInputValue(bps);
    updateChainMut.mutate({ partner_commission_bps: bps });
  }

  const anyPending = $derived(
    submitMut.isPending || priceMut.isPending || approveMut.isPending || returnMut.isPending || reviseMut.isPending || deleteMut.isPending
  );

  function discountAmountDisplay(amount: number) {
    return amount > 0 ? `-${formatMoney(amount, quote.currency)}` : formatMoney(amount, quote.currency);
  }

</script>

<div class="tym-card p-[18px]">
  <div class="font-heading font-bold text-[16px] text-navy mb-3.5">Quotation summary</div>
  <div class="flex flex-col gap-2.5 text-[13.5px]">
    <div class="flex justify-between items-center">
      <span class="text-toyama-heavy-carbon">Billing entity</span>
      {#if isAdmin}
        <select
          class="pq-num-input w-auto text-[12.5px]"
          value={quote.billing_entity}
          onchange={(e) => setBillingEntity((e.currentTarget as HTMLSelectElement).value)}
        >
          {#each BILLING_ENTITIES as entity (entity)}
            <option value={entity}>{BILLING_ENTITY_LABEL[entity as BillingEntity]}</option>
          {/each}
        </select>
      {:else}
        <span class="font-semibold text-navy text-right">{BILLING_ENTITY_LABEL[quote.billing_entity]}</span>
      {/if}
    </div>
    <div class="flex justify-between">
      <span class="text-toyama-heavy-carbon">Original price</span>
      <span class="font-semibold text-navy">{formatMoney(quote.list_subtotal_minor, quote.currency)}</span>
    </div>
    <div class="flex justify-between">
      <span class="text-toyama-heavy-carbon">Discount applied</span>
      <span class="font-semibold text-navy">
        {formatPercent(quote.customer_discount_bps)}
        <span class="text-dark-gray">· {discountAmountDisplay(quote.customer_discount_minor)}</span>
      </span>
    </div>

    {#if isAdmin}
      <div class="flex justify-between items-center">
        <span class="text-toyama-heavy-carbon">Installation</span>
        <span class="flex items-center gap-1.5">
          <input
            type="number"
            class="pq-num-input w-[52px]"
            step="0.01"
            min="0"
            max="100"
            disabled={!chainEditable}
            value={bpsToInputValue(quote.installation_percent_bps)}
            onchange={(e) => setChainField(e.currentTarget as HTMLInputElement, 'installation_percent_bps', (e.currentTarget as HTMLInputElement).value)}
          />
          <span class="text-dark-gray text-xs">%</span>
          <span class="text-dark-gray">· {formatMoney(quote.installation_amount_minor, quote.currency)}</span>
        </span>
      </div>
      <div class="flex justify-between items-center">
        <span class="text-toyama-heavy-carbon">Commissioning</span>
        <span class="flex items-center gap-1.5">
          <input
            type="number"
            class="pq-num-input w-[52px]"
            step="0.01"
            min="0"
            max="100"
            disabled={!chainEditable}
            value={bpsToInputValue(quote.commissioning_percent_bps)}
            onchange={(e) => setChainField(e.currentTarget as HTMLInputElement, 'commissioning_percent_bps', (e.currentTarget as HTMLInputElement).value)}
          />
          <span class="text-dark-gray text-xs">%</span>
          <span class="text-dark-gray">· {formatMoney(quote.commissioning_amount_minor, quote.currency)}</span>
        </span>
      </div>
      <div class="flex justify-between items-center">
        <span class="text-toyama-heavy-carbon">Of which special discount</span>
        <span class="flex items-center gap-1.5">
          <input
            type="number"
            class="pq-num-input w-[52px]"
            step="0.01"
            min="0"
            max="100"
            disabled={!chainEditable}
            value={bpsToInputValue(quote.special_discount_percent_bps)}
            onchange={(e) => setChainField(e.currentTarget as HTMLInputElement, 'special_discount_percent_bps', (e.currentTarget as HTMLInputElement).value)}
          />
          <span class="text-dark-gray text-xs">%</span>
          <span class="text-dark-gray">· {discountAmountDisplay(quote.special_discount_amount_minor)}</span>
        </span>
      </div>
      <div class="flex justify-between items-center">
        <span class="text-toyama-heavy-carbon">Tax (GST)</span>
        <span class="flex items-center gap-1.5">
          <input
            type="number"
            class="pq-num-input w-[52px]"
            step="0.01"
            min="0"
            max="100"
            disabled={!chainEditable}
            value={bpsToInputValue(quote.tax_rate_bps)}
            onchange={(e) => setChainField(e.currentTarget as HTMLInputElement, 'tax_rate_bps', (e.currentTarget as HTMLInputElement).value)}
          />
          <span class="text-dark-gray text-xs">%</span>
          <span class="text-dark-gray">· {formatMoney(quote.tax_amount_minor, quote.currency)}</span>
        </span>
      </div>
    {:else}
      {#if quote.installation_percent_bps !== 0 || quote.installation_amount_minor !== 0}
        <div class="flex justify-between">
          <span class="text-dark-gray">Installation ({formatPercent(quote.installation_percent_bps)})</span>
          <span class="text-dark-gray">{formatMoney(quote.installation_amount_minor, quote.currency)}</span>
        </div>
      {/if}
      {#if quote.commissioning_percent_bps !== 0 || quote.commissioning_amount_minor !== 0}
        <div class="flex justify-between">
          <span class="text-dark-gray">Commissioning ({formatPercent(quote.commissioning_percent_bps)})</span>
          <span class="text-dark-gray">{formatMoney(quote.commissioning_amount_minor, quote.currency)}</span>
        </div>
      {/if}
      <div class="flex justify-between">
        <span class="text-dark-gray">Tax ({(quote.tax_rate_bps / 100).toFixed(2)}%)</span>
        <span class="text-dark-gray">{formatMoney(quote.tax_amount_minor, quote.currency)}</span>
      </div>
      <div class="text-xs text-dark-gray bg-toyama-light-carbon rounded-lg px-[11px] py-2.5 leading-relaxed mt-0.5">
        {quote.designer_priced
          ? 'Self-serve vertical — set your prices and approve directly, no admin pricing needed.'
          : 'This vertical is priced by the MD. Submit your BOQ and the admin will add pricing.'}
      </div>
    {/if}

    <div class="border-t-2 border-navy mt-1.5 pt-3 flex justify-between items-center">
      <span class="font-heading font-bold text-[15px] text-navy">Final price payable</span>
      <span class="font-heading font-bold text-[22px] text-toyama-teal">{formatMoney(quote.grand_total_minor, quote.currency)}</span>
    </div>
  </div>

  <!--
    Internal margin giveaway. Toyama has up to 40% of list price to give away
    per deal, split between the customer's discount and the introducing
    partner's commission. NONE of this reaches the client: the PDF and the
    public share view carry only the customer's own leg. Read-only for anyone
    who can view the quote; only an admin may set the commission rate.
  -->
  <div
    class="mt-[18px] pt-3 {overStandardGiveawayThreshold
      ? 'border-2 border-red-600 rounded-lg p-3 bg-red-50'
      : 'border-t border-toyama-light-carbon'}"
  >
    <div class="flex items-center justify-between mb-2.5">
      <span class="font-heading font-bold text-[13px] {overStandardGiveawayThreshold ? 'text-red-700' : 'text-navy'}">
        Margin giveaway
      </span>
      <span class="tym-badge {overStandardGiveawayThreshold ? 'tym-badge-danger' : 'tym-badge-info'}">Internal only</span>
    </div>
    <p class="mb-2.5 text-xs text-dark-gray">
      {#if quote.vertical === 'hotel'}
        Hotel quotations are exempt from giveaway allowances. Admin pricing and approval are still required.
      {:else if isAdmin}
        Standard reporting threshold: {formatPercent(STANDARD_GIVEAWAY_BPS)}. Admins are exempt from edit and approval allowances.
      {:else}
        Your edit and approval allowance: {formatPercent(giveawayAllowanceBps)}.
      {/if}
    </p>
    {#if overStandardGiveawayThreshold}
      <div
        class="mb-2.5 flex items-start gap-2 rounded-lg bg-red-600 px-[11px] py-2 text-xs font-semibold leading-relaxed text-white"
        role="alert"
      >
        <TriangleAlert class="h-4 w-4 shrink-0 mt-px" aria-hidden="true" />
        <span>
          Over the standard {formatPercent(STANDARD_GIVEAWAY_BPS)} reporting threshold by {formatPercent(overBy)} — this deal gives away
          {formatPercent(quote.combined_given_bps)} of list price.
        </span>
      </div>
    {/if}
    <div class="flex flex-col gap-2 text-[12.5px]">
      <div class="flex justify-between items-center">
        <span class="text-toyama-heavy-carbon">Partner commission</span>
        {#if isAdmin}
          <span class="flex items-center gap-1.5">
            <input
              type="number"
              class="pq-num-input w-[52px]"
              step="0.01"
              min="0"
              max="100"
              disabled={!chainEditable}
              value={bpsToInputValue(quote.partner_commission_bps)}
              onchange={(e) => setPartnerCommission(e.currentTarget as HTMLInputElement, (e.currentTarget as HTMLInputElement).value)}
            />
            <span class="text-dark-gray text-xs">%</span>
            <span class="text-dark-gray">· {formatMoney(quote.partner_commission_minor, quote.currency)}</span>
          </span>
        {:else}
          <span class="text-navy">
            {formatPercent(quote.partner_commission_bps)}
            <span class="text-dark-gray">· {formatMoney(quote.partner_commission_minor, quote.currency)}</span>
          </span>
        {/if}
      </div>
      <div class="flex justify-between border-t border-toyama-light-carbon pt-2">
        <span class="font-semibold {overStandardGiveawayThreshold ? 'text-red-600' : 'text-navy'}">Combined given</span>
        <span class="font-semibold {overStandardGiveawayThreshold ? 'text-red-600' : 'text-navy'}">
          {formatPercent(quote.combined_given_bps)}
          <span class="font-normal {overStandardGiveawayThreshold ? 'text-red-600' : 'text-dark-gray'}"
            >· {formatMoney(quote.combined_given_minor, quote.currency)}</span
          >
        </span>
      </div>
    </div>
  </div>

  <div class="mt-[18px] flex flex-col gap-2.5">
    {#if canSubmit}
      <button
        type="button"
        class="btn-primary w-full !py-3"
        disabled={anyPending}
        onclick={() => runAction(() => submitMut.mutateAsync(), 'Submitted for pricing.')}
      >
        <Send class="h-4 w-4" /> Submit for pricing
      </button>
    {/if}
    {#if canPrice}
      <button
        type="button"
        class="btn-primary w-full !py-3"
        disabled={anyPending}
        onclick={() => runAction(() => priceMut.mutateAsync(), 'Pricing saved. Ready to approve.')}
      >
        <CheckCheck class="h-4 w-4" /> Save pricing
      </button>
    {/if}
    {#if canApprove}
      <button
        type="button"
        class="btn-primary w-full !py-3"
        disabled={anyPending || approveNeedsAdmin}
        onclick={() => runAction(() => approveMut.mutateAsync(), 'Quotation approved.')}
      >
        <CheckCheck class="h-4 w-4" /> Approve
      </button>
      {#if approveNeedsAdmin}
        <p class="text-center text-[11.5px] font-semibold leading-snug text-red-600">
          Over your {formatPercent(giveawayAllowanceBps)} allowance — an admin must approve this quote.
        </p>
      {/if}
    {/if}
    {#if canReturn}
      <button
        type="button"
        class="btn-secondary w-full !py-2.5"
        disabled={anyPending}
        onclick={() => runAction(() => returnMut.mutateAsync(), 'Sent back to the designer for pricing edits.')}
      >
        <Reply class="h-[15px] w-[15px]" /> Return for pricing edits
      </button>
    {/if}
    {#if canRevise}
      <button
        type="button"
        class="btn-secondary w-full !py-2.5"
        disabled={anyPending}
        onclick={() => runAction(() => reviseMut.mutateAsync(), 'Quotation reopened for revision.')}
      >
        <RotateCcw class="h-[15px] w-[15px]" /> Revise
      </button>
    {/if}
    {#if canManageShare}
      <button
        type="button"
        class="btn-secondary w-full !py-2.5"
        onclick={() => (showShareModal = true)}
      >
        <Link2 class="h-[15px] w-[15px]" /> Share link
      </button>
    {/if}
    {#if isApproved}
      <label class="flex items-center gap-1.5 text-[12.5px] text-toyama-heavy-carbon">
        <input type="checkbox" bind:checked={showDiscount} />
        Show discount on PDF
      </label>
      <button type="button" class="btn-secondary w-full !py-2.5" disabled={previewing} onclick={previewPdf}>
        <Eye class="h-[15px] w-[15px]" /> Preview PDF
      </button>
      <button type="button" class="btn-secondary w-full !py-2.5" disabled={downloading} onclick={downloadPdf}>
        <Download class="h-[15px] w-[15px]" /> Download PDF
      </button>
    {/if}
    {#if canExportXlsx}
      <button type="button" class="btn-secondary w-full !py-2.5" disabled={exportingBOQ} onclick={downloadBOQ}>
        <Sheet class="h-[15px] w-[15px]" /> Export BOQ
      </button>
      <button type="button" class="btn-secondary w-full !py-2.5" disabled={exportingXlsx} onclick={downloadXlsx}>
        <Sheet class="h-[15px] w-[15px]" /> Excel (verification)
      </button>
    {/if}
    {#if canDuplicate}
      <button
        type="button"
        class="btn-secondary w-full !py-2.5"
        disabled={duplicateMut.isPending}
        onclick={() => duplicateMut.mutate()}
      >
        <Copy class="h-[15px] w-[15px]" /> Duplicate
      </button>
    {/if}
    {#if canCreateNewLot}
      <button
        type="button"
        class="btn-secondary w-full !py-2.5"
        disabled={newLotMut.isPending}
        onclick={() => newLotMut.mutate()}
      >
        <Layers class="h-[15px] w-[15px]" /> New lot for this project
      </button>
    {/if}
    {#if isAdmin}
      <button type="button" class="btn-secondary w-full !py-2.5" onclick={() => (showVersionsModal = true)}>
        <History class="h-[15px] w-[15px]" /> Version history
      </button>
    {/if}
    {#if canDelete}
      <button
        type="button"
        class="btn-secondary w-full !py-2.5 !border-red-600 !text-red-600 hover:!bg-red-50"
        disabled={deleteMut.isPending}
        onclick={deletePermanently}
      >
        <Trash2 class="h-[15px] w-[15px]" /> {deleteMut.isPending ? 'Deleting permanently…' : 'Delete permanently'}
      </button>
    {/if}
  </div>
</div>

{#if showShareModal}
  <Modal title="Share link" onClose={() => (showShareModal = false)}>
    <ShareLinkModal {quote} onClose={() => (showShareModal = false)} />
  </Modal>
{/if}

{#if showVersionsModal}
  <Modal title="Version history" onClose={() => (showVersionsModal = false)} wide>
    <VersionHistoryModal quotationId={quote.id} onClose={() => (showVersionsModal = false)} />
  </Modal>
{/if}
